/**
 * authService - 認証サービス
 *
 * RegiInfo テーブルを使った API 認証。
 * server.js の POST /api/login エンドポイントを呼び出す。
 *
 * セッションは sessionStorage で管理するため、
 * ブラウザを閉じると自動的にログアウト状態になる。
 */

const SESSION_KEY = "loginUser";
const PERSON_KEY  = "currentPerson";

/**
 * ログイン処理
 *
 * /api/login に userID・password を POST し、
 * 成功時は { shopNo, regiNo, shopName, regiName, userName, userType } を sessionStorage に保存する。
 * currentPerson の初期値として userName をセットする。
 *
 * @param {string} userID    - ログイン画面で入力したユーザーID (RegiInfo.UserID)
 * @param {string} password  - パスワード
 * @returns {Promise<boolean>} 認証成功なら true
 */
export const login = async (userID, password) => {
  const res = await fetch('/api/login', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ userID, password }),
  });
  if (!res.ok) return false;
  const user = await res.json();
  sessionStorage.setItem(SESSION_KEY, JSON.stringify({
    shopNo:   user.shopNo,
    regiNo:   user.regiNo,
    shopName: user.shopName,
    regiName: user.regiName,
    userName: user.userName,
    userType: user.userType,
  }));
  // 担当者名の初期値を RegiInfo.UserName でセットする
  sessionStorage.setItem(PERSON_KEY, user.userName ?? "");
  return true;
};

/**
 * ログアウト処理
 */
export const logout = () => {
  sessionStorage.removeItem(SESSION_KEY);
  sessionStorage.removeItem(PERSON_KEY);
};

/**
 * ログイン状態の確認
 * @returns {boolean}
 */
export const isLoggedIn = () => {
  return sessionStorage.getItem(SESSION_KEY) !== null;
};

/**
 * 現在のログインユーザー情報を取得
 * @returns {{ shopNo: number, regiNo: number, shopName: string, regiName: string, userName: string, userType: string } | null}
 */
export const getCurrentUser = () => {
  const data = sessionStorage.getItem(SESSION_KEY);
  return data ? JSON.parse(data) : null;
};

/**
 * 現在の担当者名を取得（セッション中に変更されていればその値を返す）
 * @returns {string}
 */
export const getCurrentPerson = () => {
  return sessionStorage.getItem(PERSON_KEY) ?? "";
};

/**
 * 担当者名をセッション内で更新する
 * ログアウトまで保持され、次回ログイン時は RegiInfo.UserName にリセットされる
 * @param {string} name
 */
export const setCurrentPerson = (name) => {
  sessionStorage.setItem(PERSON_KEY, name);
};
